import { Alert, Box, Button, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import type { PromptRecord, PromptScope } from '../../shared/types/prompts';
import { useListProfilesQuery } from '../../store/api/rpcSlice';
import { useListFixtureSetsQuery } from './api/promptTestingApi';
import { BrowseColumn } from './components/BrowseColumn';
import { EditColumn } from './components/EditColumn';
import { FixtureCreationWizard } from './components/FixtureCreationWizard';
import { TestColumn } from './components/TestColumn';

export function shouldShowFixtureSetup(
  isLoadingFixtures: boolean,
  isFixtureLoadError: boolean,
  fixtures: unknown[]
): boolean {
  return !isLoadingFixtures && !isFixtureLoadError && fixtures.length === 0;
}

export interface SelectedPrompt {
  profileSlug: string;
  promptId?: string | null;
  promptSlug: string;
  baseText: string;
  promptScope: PromptScope;
}

export function shouldShowCreateFirstPersonaAction(selectedPrompt: SelectedPrompt | null): boolean {
  return selectedPrompt !== null;
}

export function promptTesterWorkflowStep(
  selectedPrompt: SelectedPrompt | null,
  fixtures: unknown[]
): 1 | 2 | 3 {
  if (!selectedPrompt) return 1;
  if (fixtures.length === 0) return 2;
  return 3;
}

export function applySavedPromptToSelection(
  current: SelectedPrompt,
  savedPrompt: PromptRecord
): { selectedPrompt: SelectedPrompt; candidateText: string } {
  return {
    selectedPrompt: {
      ...current,
      promptId: savedPrompt.id,
      promptSlug: savedPrompt.slug,
      baseText: savedPrompt.text,
      promptScope: savedPrompt.scope,
    },
    candidateText: savedPrompt.text,
  };
}

export default function PromptTesterPage() {
  const [selectedPrompt, setSelectedPrompt] = useState<SelectedPrompt | null>(null);
  const [candidateText, setCandidateText] = useState('');
  const [fixtureWizardOpen, setFixtureWizardOpen] = useState(false);
  const [preferredFixtureSetId, setPreferredFixtureSetId] = useState<string | null>(null);

  const {
    data: fixturesData,
    isLoading: isLoadingFixtures,
    isError: isFixtureLoadError,
    refetch: refetchFixtures,
  } = useListFixtureSetsQuery();
  const { data: profilesData } = useListProfilesQuery(undefined);

  const fixtures = fixturesData?.fixture_sets ?? [];
  const workflowStep = promptTesterWorkflowStep(selectedPrompt, fixtures);

  const handlePromptSelected = useCallback(
    (
      profileSlug: string,
      promptSlug: string,
      baseText: string,
      promptScope: PromptScope,
      promptId?: string | null
    ) => {
      setSelectedPrompt({ profileSlug, promptId, promptSlug, baseText, promptScope });
      setCandidateText(baseText);
    },
    []
  );

  const handleFixtureCreated = useCallback(
    (fixtureSetId: string) => {
      setPreferredFixtureSetId(fixtureSetId);
      void refetchFixtures();
    },
    [refetchFixtures]
  );

  const handlePreferredFixtureApplied = useCallback(() => {
    setPreferredFixtureSetId(null);
  }, []);

  const handlePromptSaved = useCallback((savedPrompt: PromptRecord) => {
    setSelectedPrompt((current) => {
      if (!current) return current;
      const next = applySavedPromptToSelection(current, savedPrompt);
      setCandidateText(next.candidateText);
      return next.selectedPrompt;
    });
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>
          Prompt Tester
        </Typography>
        {selectedPrompt && (
          <Typography variant="body2" color="text.secondary">
            {selectedPrompt.profileSlug} / {selectedPrompt.promptSlug}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          px: 3,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {[
          { step: 1, label: 'Choose prompt' },
          { step: 2, label: 'Create/select test persona' },
          { step: 3, label: 'Compare outputs' },
        ].map((item) => (
          <Typography
            key={item.step}
            variant="caption"
            color={workflowStep === item.step ? 'primary.main' : 'text.secondary'}
            sx={{ fontWeight: workflowStep === item.step ? 700 : 500 }}
          >
            {item.step}. {item.label}
          </Typography>
        ))}
      </Box>

      {/* 3-column layout -- all visible simultaneously */}
      {isFixtureLoadError && (
        <Alert severity="error" sx={{ m: 2 }}>
          Test personas could not be loaded. Refresh the page or try again before creating a new
          persona.
        </Alert>
      )}
      {shouldShowFixtureSetup(isLoadingFixtures, isFixtureLoadError, fixtures) && (
        <Alert
          severity="info"
          sx={{ m: 2 }}
          action={
            shouldShowCreateFirstPersonaAction(selectedPrompt) ? (
              <Button color="inherit" size="small" onClick={() => setFixtureWizardOpen(true)}>
                Create First Persona
              </Button>
            ) : undefined
          }
        >
          {shouldShowCreateFirstPersonaAction(selectedPrompt)
            ? 'No test personas exist yet. Create an anonymized test persona from a scenario before running persona-backed comparisons.'
            : 'No test personas exist yet. Select a prompt first, then create an anonymized test persona.'}
        </Alert>
      )}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: { xs: 'auto', md: 'hidden' },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Column 1: Selection (25%) */}
        <Box
          sx={{
            width: { xs: '100%', md: '25%' },
            minWidth: { xs: 0, md: 240 },
            maxWidth: { xs: 'none', md: 360 },
            borderRight: { xs: 0, md: '1px solid' },
            borderBottom: { xs: '1px solid', md: 0 },
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          <BrowseColumn
            profiles={profilesData?.profiles ?? []}
            onPromptSelected={handlePromptSelected}
            selectedPromptSlug={selectedPrompt?.promptSlug ?? null}
          />
        </Box>

        {/* Column 2: Edit / Version Control / Middleware (40%) */}
        <Box
          sx={{
            flex: 1,
            minWidth: { xs: 0, md: 400 },
            minHeight: { xs: 420, md: 0 },
            borderRight: { xs: 0, md: '1px solid' },
            borderBottom: { xs: '1px solid', md: 0 },
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {selectedPrompt ? (
            <EditColumn
              profileSlug={selectedPrompt.profileSlug}
              promptSlug={selectedPrompt.promptSlug}
              promptScope={selectedPrompt.promptScope}
              baseText={selectedPrompt.baseText}
              candidateText={candidateText}
              onCandidateChange={setCandidateText}
              onSaved={handlePromptSaved}
              onTestAndCompare={() => {}}
              onBack={() => setSelectedPrompt(null)}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 4,
              }}
            >
              <Alert severity="info" sx={{ maxWidth: 400 }}>
                Select a profile and prompt from the list on the left. Then create or choose an
                anonymized test persona for comparisons.
              </Alert>
            </Box>
          )}
        </Box>

        {/* Column 3: Test Results (35%) */}
        <Box
          sx={{
            width: { xs: '100%', md: '35%' },
            minWidth: { xs: 0, md: 320 },
            maxWidth: { xs: 'none', md: 500 },
            minHeight: { xs: 420, md: 0 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {selectedPrompt ? (
            <TestColumn
              profileSlug={selectedPrompt.profileSlug}
              promptSlug={selectedPrompt.promptSlug}
              candidateText={candidateText}
              fixtures={fixtures}
              preferredFixtureSetId={preferredFixtureSetId}
              onPreferredFixtureApplied={handlePreferredFixtureApplied}
              onBack={() => {}}
              onCreateFixture={() => setFixtureWizardOpen(true)}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 4,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Test results will appear here after you select a prompt and run a comparison.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <FixtureCreationWizard
        open={fixtureWizardOpen}
        profileSlug={selectedPrompt?.profileSlug ?? ''}
        onClose={() => setFixtureWizardOpen(false)}
        onCreated={handleFixtureCreated}
      />
    </Box>
  );
}
